import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../context/authStore';
import toast from 'react-hot-toast';
import './Navbar.css';

function Navbar() {
  const { user, userDoc, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success('تم تسجيل الخروج');
    navigate('/');
    setDropdownOpen(false);
    setMobileOpen(false);
  };

  const isActive = (path) =>
    path.startsWith('/#')
      ? false
      : location.pathname === path || location.pathname.startsWith(path + '/');

  const navLinks = [
    { to: '/courses',  label: 'الكورسات', icon: '📚' },
    { to: '/teachers', label: 'المدرسون', icon: '👨‍🏫' },
    { to: '/reels',    label: '🎬 ريلز',  icon: '' },
    { to: '/#features', label: 'المميزات', icon: '✨' },
  ];

  const dashboardLink = userDoc?.role === 'admin' ? '/admin' : '/dashboard';
  const dashboardLabel = userDoc?.role === 'admin' ? '🛡️ الأدمن' : '⚙️ لوحتي';

  const [search, setSearch] = useState('');
  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/courses?search=${encodeURIComponent(search.trim())}`);
    setSearch('');
    setMobileOpen(false);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo">مدرسك</Link>

          {/* Search Bar */}
          <form className="nav-search-form" onSubmit={handleSearch}>
            <input
              type="text"
              className="nav-search-input"
              placeholder="ابحث عن كورس أو مدرّس..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="submit" className="nav-search-btn">🔍</button>
          </form>

          {/* Desktop links */}
          <div className="navbar-links">
            {navLinks.map(l => (
              <Link key={l.to} to={l.to} className={isActive(l.to) ? 'active' : ''}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Desktop actions */}
          <div className="navbar-actions">
            {user ? (
              <>
                <Link to={dashboardLink} className="btn btn-outline btn-sm">
                  {dashboardLabel}
                </Link>
                <div className="nav-avatar-wrap" ref={dropdownRef}>
                  <button
                    className="nav-avatar-btn"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    aria-label="قائمة المستخدم"
                  >
                    <div className="avatar" style={{ width: 36, height: 36, fontSize: 14, overflow: 'hidden', padding: 0 }}>
                      {userDoc?.avatar
                        ? <img src={userDoc.avatar} alt={userDoc.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                        : (userDoc?.name?.[0] || '؟')
                      }
                    </div>
                  </button>
                  {dropdownOpen && (
                    <div className="nav-dropdown">
                      <div className="nav-dropdown-header">
                        <strong>{userDoc?.name}</strong>
                        <span className="badge badge-blue" style={{ fontSize: 11 }}>
                          {userDoc?.role === 'teacher' ? 'مدرّس' : userDoc?.role === 'admin' ? 'أدمن' : 'طالب'}
                        </span>
                      </div>
                      <Link to="/dashboard" onClick={() => setDropdownOpen(false)}>⚙️ لوحة التحكم</Link>
                      <Link to="/courses"   onClick={() => setDropdownOpen(false)}>📚 الكورسات</Link>
                      <Link to="/reels"     onClick={() => setDropdownOpen(false)}>🎬 ريلز</Link>
                      {userDoc?.role === 'teacher' && (
                        <Link to={`/teachers/${userDoc?.username || userDoc?.uid}`} onClick={() => setDropdownOpen(false)}>👤 بروفايلي</Link>
                      )}
                      {userDoc?.role === 'admin' && (
                        <Link to="/admin" onClick={() => setDropdownOpen(false)}>🛡️ لوحة الأدمن</Link>
                      )}
                      <button onClick={handleLogout} className="logout-btn">🚪 تسجيل الخروج</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline btn-sm">تسجيل الدخول</Link>
                <Link to="/register?role=teacher" className="btn btn-primary btn-sm">انضم كمدرّس</Link>
              </>
            )}
          </div>

          {/* Hamburger */}
          <button
            className="hamburger"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="القائمة"
          >
            <span className={`ham-bar ${mobileOpen ? 'open' : ''}`} />
            <span className={`ham-bar ${mobileOpen ? 'open' : ''}`} />
            <span className={`ham-bar ${mobileOpen ? 'open' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="mobile-menu">
          {/* User info strip */}
          {user && (
            <div className="mobile-user-info">
              <div className="avatar" style={{ width: 38, height: 38, fontSize: 15, overflow: 'hidden', padding: 0, flexShrink: 0 }}>
                {userDoc?.avatar
                  ? <img src={userDoc.avatar} alt={userDoc.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  : (userDoc?.name?.[0] || '؟')
                }
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{userDoc?.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {userDoc?.role === 'teacher' ? 'مدرّس' : userDoc?.role === 'admin' ? 'أدمن' : 'طالب'}
                </div>
              </div>
            </div>
          )}

          <div className="mobile-menu-links">
            <Link to="/"         className={`mobile-link ${isActive('/') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>🏠 الرئيسية</Link>
            <Link to="/courses"  className={`mobile-link ${isActive('/courses') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>📚 الكورسات</Link>
            <Link to="/teachers" className={`mobile-link ${isActive('/teachers') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>👨‍🏫 المدرسون</Link>
            <Link to="/reels"    className={`mobile-link ${isActive('/reels') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>🎬 ريلز</Link>
          </div>

          <div className="mobile-menu-actions">
            {user ? (
              <>
                <Link to={dashboardLink} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setMobileOpen(false)}>
                  {dashboardLabel}
                </Link>
                {userDoc?.role === 'teacher' && (
                  <Link to={`/teachers/${userDoc?.username || userDoc?.uid}`} className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setMobileOpen(false)}>
                    👤 بروفايلي العام
                  </Link>
                )}
                <button onClick={handleLogout} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', color: 'var(--danger)' }}>
                  🚪 تسجيل الخروج
                </button>
              </>
            ) : (
              <>
                <Link to="/login"            className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setMobileOpen(false)}>تسجيل الدخول</Link>
                <Link to="/register?role=student"  className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setMobileOpen(false)}>🎓 سجّل كطالب</Link>
                <Link to="/register?role=teacher" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setMobileOpen(false)}>👨‍🏫 انضم كمدرّس</Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
